<template>
  <div class="min-w-[500px] overflow-auto h-full">
    <div class="my-4 lg:my-8">
      <!-- title -->
      <div class="task-1 flex-grow flex items-center">
        <svg
          viewBox="0 0 40 40"
          class="block w-8 h-8 stroke-current text-navy"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)">
            <path
              d="M13.045,18.636l-3.712.53.53-3.712,9.546-9.546A2.25,2.25,0,0,1,22.591,9.09Z"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M5.250 0.749 L12.750 0.749 L12.750 3.749 L5.250 3.749 Z"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M12.75,2.249h3a1.5,1.5,0,0,1,1.5,1.5"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M17.25,18.749v3a1.5,1.5,0,0,1-1.5,1.5H2.25a1.5,1.5,0,0,1-1.5-1.5v-18a1.5,1.5,0,0,1,1.5-1.5h3"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M5.25 8.249L12.75 8.249"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M5.25 12.749L8.25 12.749"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
          </g>
        </svg>

        <span class="ml-2 text-navy font-bold leading-7 text-2xl">
          Edit Task Record
        </span>
      </div>
    </div>

    <div class="mt-4 shadow rounded-lg text-sm">
      <div class="py-4 px-8 bg-white">
        <!-- Sample ID -->
        <div class="w-1/2 mt-8">
          <label
            class="block text-sm font-medium text-gray-700"
            for="sample_id"
          >
            Sample ID
          </label>

          <input
            class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            id="sample_id"
            type="text"
            required
            v-model="form.sample_id"
          />
          <div class="text-red-600" v-if="form.errors.sample_id">
            {{ form.errors.sample_id }}
          </div>
        </div>

        <div class="mt-4">
          <label
            class="block text-sm font-medium text-gray-700"
            for="lab_tests"
          >
            {{ sample.lab }} Lab Tests
          </label>

          <Multiselect
            v-model="form.tests"
            :options="sample.farm_tests"
            :allow-empty="required"
            :max="1"
            mode="tags"
          />
          <div class="text-red-600" v-if="form.errors.tests">
            {{ form.errors.tests }}
          </div>
        </div>

        <div class="mt-4">
          <label
            class="block text-sm font-medium text-gray-700"
            for="farm"
          >
            Farm
          </label>

          <select
            class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            id="farm"
            v-model="form.farm"
            @change="onFarmChange"
          >
            <option value="">Select Farm</option>
            <option
              v-for="farm in sample.farms"
              :value="farm.id"
              :key="farm.id"
            >
              {{ farm.name }}
            </option>
          </select>

          <div class="text-red-600" v-if="form.errors.farm">
            {{ form.errors.farm }}
          </div>
        </div>

        <div class="mt-4">
          <label
            class="block text-sm font-medium text-gray-700"
            for="sample_zone"
          >
            Sample Zone
          </label>
          <select
            class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            id="sample_zone"
            v-model="form.sample_zone"
            :disabled="!form.farm"
          >
            <option value="">{{ form.farm ? 'Not Matched' : 'Select Farm First' }}</option>
            <option
              v-for="(name, key) in filteredSampleZones"
              :value="key"
              :key="key"
              :class="{
                'font-semibold text-blue-700': name.includes('(Currently Assigned)')
              }"
            >
              {{ name }}
            </option>
          </select>
          
          <p class="mt-1 text-sm text-gray-600">
            <span v-if="!form.farm" class="text-gray-500">
              Please select a farm first to see available zones.
            </span>
            <span v-else>
              Only showing zones for the selected farm that haven't been assigned to other samples in this task.
              <span v-if="Object.keys(filteredSampleZones).length === 0" class="text-amber-600">
                No available zones found for this farm.
              </span>
            </span>
          </p>

          <div class="text-red-600" v-if="form.errors.sample_zone">
            {{ form.errors.sample_zone }}
          </div>
        </div>

        <div class="mt-4">
          <label
            class="block text-sm font-medium text-gray-700"
            for="lpis_field"
          >
            LPIS Parcel ID
          </label>

          <!-- ISEC users get dropdown with possible LPIS values -->
          <div v-if="sample.organisation_name === 'ISEC'">
            <select
              v-if="possible_lpis.length > 0"
              class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              id="lpis_field"
              v-model="form.lpis"
            >
              <option value="">Select LPIS</option>
              <option
                v-for="(single_lpis, key) in possible_lpis"
                :value="single_lpis.properties.parcel_id"
                :key="key"
              >
                {{ single_lpis.properties.parcel_id }}
              </option>
            </select>
            
            <!-- Fallback input for ISEC when no LPIS options available -->
            <input
              v-else
              type="text"
              class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              id="lpis_field"
              v-model="form.lpis"
              placeholder="Enter LPIS Parcel ID (no options available)"
            />
          </div>

          <!-- Non-ISEC users get text input for manual LPIS entry -->
          <input
            v-else
            type="text"
            class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            id="lpis_field"
            v-model="form.lpis"
            placeholder="Enter LPIS Parcel ID (optional)"
          />

          <!-- Help text -->
          <p class="mt-1 text-sm text-gray-600">
            <span v-if="sample.organisation_name === 'ISEC'">
              <span v-if="possible_lpis.length > 0">
                ISEC: Select LPIS from automatically determined options for this farmer.
              </span>
              <span v-else>
                ISEC: No LPIS options found for this farmer. You can enter manually if needed.
              </span>
            </span>
            <span v-else>
              Enter LPIS Parcel ID manually if available.
            </span>
          </p>
          


          <div class="text-red-600" v-if="form.errors.lpis">
            {{ form.errors.lpis }}
          </div>
        </div>

        <div class="mt-4">
          <label
            class="block text-sm font-medium text-gray-700"
            for="soil_type"
          >
            Soil Type
          </label>

          <select
            class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            id="soil_type"
            v-model="form.soil_type"
          >
            <option value="">Not set</option>
            <option
              v-for="soilType in sample.soil_types"
              :value="soilType"
              :key="soilType"
            >
              {{ soilType }}
            </option>
          </select>

          <div class="text-red-600" v-if="form.errors.soil_type">
            {{ form.errors.soil_type }}
          </div>
        </div>

        <div class="mt-8 flex justify-between">
          <div>
            <inertia-link
              :href="
                route('app.task.view', { task: sample.task_id, tab: 'records' })
              "
              class="inline-flex justify-center py-2 px-4 mr-3 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Cancel
            </inertia-link>
          </div>

          <div>
            <button
              type="submit"
              class="inline-flex justify-center py-2 px-4 mr-3 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-200"
              :disabled="form.sample_id == ''"
              v-on:click="update"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppLayout from "@/Layouts/App";
import Multiselect from "@vueform/multiselect";
import { useForm } from "@inertiajs/inertia-vue3";

export default {
  name: "editRecord",
  components: {
    Multiselect,
  },
  layout: [AppLayout],
  props: {
    sample: Object,
    possible_lpis: Array,
  },

  setup() {
    const form = useForm({
      sample_id: "",
      tests: [],
      sample_zone: "",
      farm: "",
      lpis: "",
      soil_type: "",
      update_georeferences: false, // New field for user confirmation
    });

    return { form };
  },

  data() {
    return {
      originalSampleZone: null, // Track original zone for change detection
    };
  },

  created() {
    this.form.sample_id = this.sample.sample_id;
    this.form.sample_zone = this.sample.sample_zone;
    this.form.tests = this.sample.lab_tests;
    this.form.farm = this.sample.farm_id;
    this.form.lpis = this.sample.lpis;
    this.form.soil_type = this.sample.soil_type || "";
    
    // Store the original sample zone for comparison
    this.originalSampleZone = this.sample.sample_zone;
    

  },

  computed: {
    lpisDebugInfo() {
      return {
        isISEC: this.sample.organisation_name === 'ISEC',
        hasLpisOptions: this.possible_lpis && this.possible_lpis.length > 0,
        lpisCount: this.possible_lpis ? this.possible_lpis.length : 0,
        currentLpis: this.form.lpis
      };
    },
    
    hasManualGeoreferences() {
      return this.sample.manual_georeferences && 
             Array.isArray(this.sample.manual_georeferences) && 
             this.sample.manual_georeferences.length > 0;
    },
    
    sampleZoneChanged() {
      return this.form.sample_zone !== this.originalSampleZone;
    },
    
    shouldPromptForGeoreferenceUpdate() {
      return this.sampleZoneChanged && this.hasManualGeoreferences;
    },

    filteredSampleZones() {
      if (!this.form.farm) {
        return {};
      }
      
      // Return zones for the selected farm
      return this.sample.sample_zones_by_farm[this.form.farm] || {};
    }
  },

  methods: {
    goTo(url) {
      window.location.href = url;
    },

    onFarmChange() {
      // Clear the selected zone when farm changes
      this.form.sample_zone = "";
    },

    async update() {
      // Check if we need to prompt for georeference update
      if (this.shouldPromptForGeoreferenceUpdate) {
        const result = await this.$swal.fire({
          title: 'Update Georeferences?',
          html: `
            <p>You've changed the parcel/zone assignment for this sample.</p>
            <p class="mt-2">This sample has <strong>${this.sample.manual_georeferences.length} georeference point(s)</strong>.</p>
            <p class="mt-2">Would you like to automatically update them to the new parcel's center location?</p>
          `,
          icon: 'question',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Yes, update georeferences',
          denyButtonText: 'No, keep current locations',
          cancelButtonText: 'Cancel edit',
          confirmButtonColor: '#3085d6',
          denyButtonColor: '#6b7280',
          cancelButtonColor: '#d33',
        });
        
        if (result.isDismissed) {
          // User cancelled, don't proceed with update
          return;
        }
        
        // Set the user's choice
        this.form.update_georeferences = result.isConfirmed;
      }

      this.form.put(route("app.task.edit-record", this.sample.id), {
        onSuccess: () => {},
      });
    },
  },
};
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
